import React, { FC, useCallback, useMemo } from 'react';
import styles from './index.module.scss';
import SearchGroup from './SearchGroup';
import { Alert, Button, Form, Skeleton } from 'src/common';
import { useTranslation } from 'react-i18next';
import { SearchProfileTermsSearch, ISearchProfile, ISearchSettings, IHandledDocument } from '../types';
import { AggregateBy } from './AggregateSelect/types';
import AggregateSelect from './AggregateSelect';
import { IProcurementFile } from 'src/models/procurements/Tender/types';
import DocumentsGroup from './DocumentsGroup';
import { notNull } from 'src/helpers/typescript';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog } from '@fortawesome/pro-light-svg-icons';
import MPSearchGroup from './MpSearchGroup';
import useMpSearchProfileId from 'src/reactiveVars/MpSearchProfileIdVar';
import { useDefaultSearchProfileFeature } from '../hooks';
export interface ISidebarProps {
  error?: Error;
  loading: boolean;
  searchProfiles: ISearchProfile[];
  aggregateByInitialValue: AggregateBy;
  files: readonly IProcurementFile[];
  documents: IHandledDocument[];
  settings: ISearchSettings;
  onSettingsChange: (settings: ISearchSettings) => void;
  toggleSearchProfilesVisible: (nextValue?: boolean) => void;
}

// It could be not true but it makes using form simpler
// FIXME: just fixme pls
interface IExpectedFormState {
  aggregateBy: AggregateBy;
  documents: string[] | undefined;
  groups: Record<string, string[] | undefined>;
}
export const Sidebar: FC<ISidebarProps> = props => {
  const {
    error,
    loading,
    searchProfiles,
    files,
    aggregateByInitialValue,
    documents,
    onSettingsChange,
    settings,
    toggleSearchProfilesVisible
  } = props;
  const { t } = useTranslation();

  const isDefaultSearchProfileFeature = useDefaultSearchProfileFeature();

  const handleFormValuesChange = useCallback(
    (values: IExpectedFormState) => {
      const settings: ISearchSettings = {
        aggregateBy: values.aggregateBy,
        groups: Object.entries(values.groups || {})
          .map(([groupId, terms]): SearchProfileTermsSearch | null => {
            const searchProfile = searchProfiles.find(searchProfile => searchProfile.id === groupId);
            return !searchProfile ? null : { searchProfile, terms: terms || [] };
          })
          .filter(notNull),
        documents: values.documents || []
      };
      onSettingsChange(settings);
    },
    [onSettingsChange, searchProfiles]
  );

  const handleValuesChanges = useCallback(
    (_, allValues: Record<string, unknown>) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      handleFormValuesChange(allValues as any);
    },
    [handleFormValuesChange]
  );
  const initialValues = useMemo(
    () => ({
      aggregateBy: aggregateByInitialValue
    }),
    [aggregateByInitialValue]
  );

  const [form] = Form.useForm();

  const onSelectGroup = useCallback(
    (groupName: string, selectedValue?: string[]) => {
      const currentFormState = form.getFieldsValue();
      if (!currentFormState.groups) return;
      currentFormState.groups[groupName] = selectedValue;
      form.setFieldsValue(currentFormState);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      handleFormValuesChange(currentFormState as any);
    },
    [form, handleFormValuesChange]
  );

  const onToggleSearchProfilesVisible = useCallback(() => {
    toggleSearchProfilesVisible();
  }, [toggleSearchProfilesVisible]);

  const [, setMpSearchGroupId] = useMpSearchProfileId();

  const onRemoveMpSearchGroup = useCallback(() => {
    setMpSearchGroupId(undefined);
  }, [setMpSearchGroupId]);

  const originalSearchProfiles = useMemo(() => {
    return searchProfiles.filter(group => !group.isMpBasedGroup);
  }, [searchProfiles]);

  const mpBasedSearchProfiles = useMemo(() => {
    return searchProfiles.filter(group => !!group.isMpBasedGroup);
  }, [searchProfiles]);

  return (
    <div className={styles.container}>
      <div className={styles.inner}>
        {loading ? (
          <Skeleton loading active />
        ) : (
          <Form form={form} onValuesChange={handleValuesChanges} initialValues={initialValues}>
            <div>
              {!!error ? (
                <Alert message={t(error.message)} type="error" showIcon />
              ) : (
                <>
                  <>
                    <div className={styles.topbarMenu}>
                      <span>{t('Common.sortBy')}</span>
                      <AggregateSelect />
                      <DocumentsGroup files={files} handledDocuments={documents} settings={settings} />
                    </div>

                    <div className={styles.button}>
                      <Button
                        type={'link'}
                        icon={<FontAwesomeIcon icon={faCog} />}
                        onClick={onToggleSearchProfilesVisible}
                      >
                        {t('KeywordSearch.editSearchWords')}
                      </Button>
                    </div>
                  </>
                  <div className={styles.unit}>
                    {originalSearchProfiles.map(group => (
                      <SearchGroup
                        selectedValue={(form.getFieldValue('groups') && form.getFieldValue('groups')[group.id]) || []}
                        onUpdateGroup={onSelectGroup}
                        key={group.id}
                        group={group}
                        handledDocuments={documents}
                        settings={settings}
                      />
                    ))}
                    {isDefaultSearchProfileFeature && (
                      <div>
                        <div className={styles.mpSection}>
                          <h3 className={styles.mpSectionTitle}>
                            {t('KeywordSearch.expandFromMPKeywordsDescription')}
                          </h3>
                          <MPSearchGroup />
                        </div>
                        <div className={styles.unit}>
                          {mpBasedSearchProfiles.map(group => (
                            <SearchGroup
                              selectedValue={
                                (form.getFieldValue('groups') && form.getFieldValue('groups')[group.id]) || []
                              }
                              onUpdateGroup={onSelectGroup}
                              key={group.id}
                              group={group}
                              handledDocuments={documents}
                              settings={settings}
                              onRemove={onRemoveMpSearchGroup}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </Form>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
